import * as React from 'react';
import Box from '@mui/material/Box';
import Modal from '@mui/material/Modal';
import {
    ButtonGroup,
    FormControl,
    FormLabel,
    Grid,
    InputLabel,
    TextField,
    Typography,
} from '@mui/material';
import {
    AppOutlinedRadioInput,
    AppPrimaryButton,
    AppFormikTextArea,
    AppFormikSelect,
    AppFormikDatePicker,
    AppFormikTextField,
    AppFormikRadioGroup,
} from 'shared/components/AppComponents';
// import { SwitchComp } from 'shared/components/Switch';
import { ClientService } from 'services';
import { useLocation, useNavigate } from 'react-router-dom';
import { RawData } from '../../listing-packets/components/VirtualizedTable';
import { ManagePacketService } from 'services/ManagePacketService';
import { useFormikContext } from 'formik';
import {
    FormikPropsFixPacketValues,
    NotExpectedMeta,
} from '../NotExpectedPage';
import { Utils } from 'shared/helpers';
import { areWeTestingWithJest } from 'test/helpers/formik';
import { createDateAsUTC } from 'shared/helpers/AgeOfDate';

const ReworkModal: React.FC<Props> = React.forwardRef(
    (
        {
            callbackClosingModal,
            callbackOpenModal,
            document,
            clientId,
            packetId,
        },
        ref
    ): React.ReactElement => {
        const { values, getFieldHelpers, validateForm, setFieldTouched } =
            useFormikContext<FormikPropsFixPacketValues>();
        const NotExpectedMetaProps = React.useContext(NotExpectedMeta);
        const location = useLocation();
        const [predefinedMessage, setPredefinedMessage] =
            React.useState<string>('');
        const [, setPredefined] = React.useState<string>('');
        const [Solicitors, SetSolicitors] = React.useState<string[]>([]);
        const [SolicitorsData, SetSolicitorsData] = React.useState<
            SolicitorData[]
        >([]);
        const navigate = useNavigate();
        const [SolicitorDisplayed, SetSolicitorDisplayed] =
            React.useState<SolicitorData>();
        const [open, setOpen] = React.useState(false);
        const [openDetail, setOpenDetail] = React.useState(true);
        const [isSaved, SetDetailSaved] = React.useState<boolean>(false);
        const [openDetailTextArea] = React.useState(true);

        /**
         * handle open modal
         */
        const handleOpen = React.useCallback((): void => {
            setOpen(true);
            if (typeof callbackOpenModal === 'function') callbackOpenModal();
        }, [callbackOpenModal]);

        /**
         * handle close modal
         */
        const handleClose = React.useCallback((): void => {
            if (!isSaved) {
                setOpen(false);
                if (typeof callbackClosingModal === 'function')
                    callbackClosingModal(null);
            }
        }, [callbackClosingModal, isSaved]);

        /**
         * handle closing rework after successful send a request
         */
        const handleCloseRework = React.useCallback((): void => {
            NotExpectedMetaProps?.setActiveBackNavigation(true);
            navigate(-1);
        }, [NotExpectedMetaProps, navigate]);

        /**
         * handle confirm
         */
        const handleConfirm = React.useCallback(async () => {
            const validate = await validateForm()
                .then((response) => {
                    if (
                        response &&
                        (Object.keys(response).includes('solicitor') ||
                            Object.keys(response).includes('reworktext') ||
                            Object.keys(response).includes('reasonby') ||
                            Object.keys(response).includes('datepickerRework'))
                    ) {
                        setFieldTouched('solicitor', true);
                        setFieldTouched('reworktext', true);
                        setFieldTouched('reasonby', true);
                        setFieldTouched('datepickerRework', true);
                        return false;
                    }

                    return true;
                })
                .catch(() => false);

            if (
                document &&
                validate &&
                SolicitorsData.some(
                    (o) => `${o.name} - ${o.email.id}` === values.solicitor
                )
            ) {
                const solicitor = SolicitorsData.find(
                    (o) => `${o.name} - ${o.email.id}` === values.solicitor
                );
                if (solicitor) {
                    const {
                        // eslint-disable-next-line @typescript-eslint/no-unused-vars
                        placeholder: b,
                        _id: documentId,
                        // eslint-disable-next-line @typescript-eslint/no-unused-vars
                        processedFile: ps,
                        // eslint-disable-next-line @typescript-eslint/no-unused-vars
                        actions: noaction,
                        // eslint-disable-next-line @typescript-eslint/no-unused-vars
                        group: g,
                        // eslint-disable-next-line @typescript-eslint/no-unused-vars
                        groupName: gi,
                        // eslint-disable-next-line @typescript-eslint/no-unused-vars
                        allowReplaceDocument: ar,
                        // eslint-disable-next-line @typescript-eslint/no-unused-vars
                        unexpectedFileType: x,
                        // eslint-disable-next-line @typescript-eslint/no-unused-vars
                        groups: gx,
                        // eslint-disable-next-line @typescript-eslint/no-unused-vars
                        isDuplicate: j,
                        // eslint-disable-next-line @typescript-eslint/no-unused-vars
                        variation: o,
                        ..._document
                    } = document;
                    const documents = [
                        {
                            ..._document,
                            documentId,
                            isMissingDocument: false,
                            statusMessage: values.reworktext
                                ? values.reworktext
                                : undefined,
                            predefinedMessage,
                        },
                    ];
                    ManagePacketService.sendToRework({
                        documents,
                        clientId,
                        packetId,
                        solicitor: {
                            solicitorId: solicitor.solicitorId,
                            contactId: solicitor.contactId,
                            name: solicitor.name,
                        },
                        expiryDate: values.datepickerRework
                            ? createDateAsUTC(
                                  new Date(values.datepickerRework)
                              ).toISOString()
                            : '',
                    })
                        .then((response) => {
                            Utils.handleSuccessResponse(response);
                            SetSolicitorDisplayed(solicitor);
                            SetDetailSaved(true);
                        })
                        .catch((error) => {
                            Utils.handleErrorResponse(error);
                        });
                }
            }
        }, [
            SolicitorsData,
            clientId,
            document,
            packetId,
            predefinedMessage,
            setFieldTouched,
            validateForm,
            values.datepickerRework,
            values.reworktext,
            values.solicitor,
        ]);

        React.useEffect(() => {
            const fieldHelper = areWeTestingWithJest()
                ? { setValue: (test: any) => test }
                : getFieldHelpers('isReworkModal');
            if (open || areWeTestingWithJest()) {
                ClientService.getSolicitors({
                    clientId: (location.state as RawData).clientId,
                }).then((response: SolicitorsListResponse) => {
                    SetSolicitorsData(response.data.data);
                    SetSolicitors(
                        Array.from(response.data.data).map(
                            (element) => `${element.name} - ${element.email.id}`
                        )
                    );
                });
            }
            fieldHelper.setValue(open);
        }, [location.state, open]);

        React.useLayoutEffect(() => {
            const fieldHelper = getFieldHelpers('isReworkReason');
            fieldHelper.setValue(openDetailTextArea);
        }, [getFieldHelpers, openDetailTextArea]);

        // const handleChange = React.useCallback(
        //     (event: React.ChangeEvent<HTMLInputElement>) => {
        //         setOpenDetailTextArea(event.target.checked);
        //     },
        //     [openDetailTextArea]
        // );

        const handleClosingMouse = React.useCallback(() => {
            if (!openDetail) {
                setOpen(false);
            }
        }, [openDetail]);

        React.useLayoutEffect(() => {
            if (!openDetail) {
                handleClose();
            }
        }, [openDetail]);

        const handleChangePredefinedMessage = React.useCallback(
            (event: any) => {
                const value =
                    typeof event === 'string' ? event : event.target.value;

                switch (value) {
                    case 'blurred':
                        setPredefinedMessage('Document blurred');
                        break;
                    case 'wrong':
                        setPredefinedMessage('Document Wrong');
                        break;
                    case 'cropped':
                        setPredefinedMessage('Document cropped');
                        break;
                    case 'corrupted':
                        setPredefinedMessage('Document corrupted');
                        break;
                    case 'datawrong':
                        setPredefinedMessage('Data / Information');
                        break;
                    case 'other':
                        setPredefinedMessage('Other');
                        break;
                    default:
                        break;
                }

                setPredefined(value);
            },
            []
        );
        // React.useLayoutEffect(() => {
        //     const fieldHelper = getFieldHelpers('reworktext');
        //     fieldHelper.setValue(predefinedMessage);
        // }, [predefinedMessage]);

        React.useImperativeHandle(
            ref,
            () => ({
                setClose: () => {
                    handleClose();
                },
                setOpen: () => {
                    handleOpen();
                },
            }),
            [handleOpen, handleClose]
        );
        return (
            <Box>
                <Modal
                    disableEscapeKeyDown={isSaved}
                    open={open || areWeTestingWithJest()}
                    onClose={handleClose}
                    aria-labelledby="parent-modal-title"
                    aria-describedby="parent-modal-description"
                    disableScrollLock
                >
                    <Box className="container-document-modal-rework">
                        <AppFormikTextField
                            name="isReworkModal"
                            inputProps={{
                                type: 'hidden',
                            }}
                            className="isReworkModal"
                        />
                        <AppFormikTextField
                            name="isReworkReason"
                            inputProps={{
                                type: 'hidden',
                            }}
                            defaultValue={openDetailTextArea}
                            className="isReworkReason"
                        />
                        <Typography variant="h2" id="parent-modal-title">
                            {isSaved
                                ? `Rework requested successfully`
                                : `Are you sure that you want to send these documents
                            for Rework?`}
                        </Typography>
                        {!isSaved && (
                            <Box className="action-tab-selection-rework-detail">
                                <ButtonGroup
                                    className="button-group-action-selection-rework"
                                    variant="contained"
                                    aria-label="outlined primary button group"
                                >
                                    <AppPrimaryButton
                                        testid="no-rework-button"
                                        title="No"
                                        className={
                                            !openDetail
                                                ? 'btn-small'
                                                : 'btn-small not-selected'
                                        }
                                        onMouseDown={handleClosingMouse}
                                        onClick={() => setOpenDetail(false)}
                                    />
                                    <AppPrimaryButton
                                        testid="yes-rework-button"
                                        title="Yes"
                                        disabled={!document}
                                        className={
                                            openDetail
                                                ? 'btn-small'
                                                : 'btn-small not-selected'
                                        }
                                        onClick={() => setOpenDetail(true)}
                                    />
                                </ButtonGroup>
                            </Box>
                        )}
                        {openDetail && !isSaved && (
                            <Box className="container-document-rework-detail">
                                <Grid
                                    container
                                    spacing={2}
                                    className="categorise-container-selection"
                                >
                                    <Grid
                                        item
                                        xs={8}
                                        className="categorise-selection categorise-item"
                                    >
                                        <InputLabel id="rework-selection-label">
                                            Select a Reviewer
                                        </InputLabel>
                                        <AppFormikSelect
                                            name="solicitor"
                                            className="rework-selection"
                                            notched={false}
                                            labelId="fix-selection-label"
                                            testid="solicitor-dropdown"
                                            MenuProps={{
                                                className:
                                                    'menu-fix-selection single-fix-rework',
                                            }}
                                            label="Reviewer"
                                            items={Solicitors}
                                        />
                                    </Grid>
                                    <Grid
                                        item
                                        xs={4}
                                        className="dateselection categorise-item"
                                    >
                                        <InputLabel id="rework-datepicker-label">
                                            Expiry Date
                                        </InputLabel>
                                        <AppFormikDatePicker
                                            name="datepickerRework"
                                            disableToday
                                            className="datepicker-rework"
                                            disablePast
                                            testid="datepicker-dropdown"
                                            InputProps={{
                                                name: 'datepickerRework',
                                                'aria-labelledby':
                                                    'rework-datepicker-label',
                                                notched: false,
                                            }}
                                            value={undefined}
                                            // onChange={() => console.log('date')}
                                            renderInput={(props: any) => (
                                                <TextField {...props} />
                                            )}
                                        />
                                    </Grid>
                                </Grid>

                                <FormControl id="reason-by-radio-group">
                                    <FormLabel id="radio-buttons-group-label-reason">
                                        Select the reason *
                                    </FormLabel>
                                    <AppFormikRadioGroup
                                        aria-labelledby="radio-buttons-group-label-reason"
                                        name="reasonby"
                                    >
                                        <Grid container spacing={2}>
                                            <Grid item xs={6}>
                                                <AppOutlinedRadioInput
                                                    value="blurred"
                                                    label="Document blurred"
                                                    onChange={
                                                        handleChangePredefinedMessage
                                                    }
                                                />
                                            </Grid>
                                            <Grid item xs={6}>
                                                <AppOutlinedRadioInput
                                                    value="wrong"
                                                    label="Document wrong"
                                                    onChange={
                                                        handleChangePredefinedMessage
                                                    }
                                                />
                                            </Grid>

                                            <Grid item xs={6}>
                                                <AppOutlinedRadioInput
                                                    value="cropped"
                                                    label="Document cropped"
                                                    onChange={
                                                        handleChangePredefinedMessage
                                                    }
                                                />
                                            </Grid>
                                            <Grid item xs={6}>
                                                <AppOutlinedRadioInput
                                                    value="corrupted"
                                                    label="Document corrupted"
                                                    onChange={
                                                        handleChangePredefinedMessage
                                                    }
                                                />
                                            </Grid>

                                            <Grid item xs={6}>
                                                <AppOutlinedRadioInput
                                                    value="datawrong"
                                                    label="Data / Information"
                                                    onChange={
                                                        handleChangePredefinedMessage
                                                    }
                                                />
                                            </Grid>
                                            <Grid item xs={6}>
                                                <AppOutlinedRadioInput
                                                    value="other"
                                                    label="Other"
                                                    onChange={
                                                        handleChangePredefinedMessage
                                                    }
                                                />
                                            </Grid>
                                        </Grid>
                                    </AppFormikRadioGroup>
                                </FormControl>

                                {/* <Grid
                                    container
                                    spacing={0}
                                    className="custom-solicitor-flag"
                                >
                                    <Grid item xs={10}>
                                        <Typography
                                            variant="h5"
                                            className="solicitor-flag-detail"
                                        >
                                            Add custom message to the solicitor
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={2} className="switch-rework">
                                        <SwitchComp
                                            className="switch-rework-solicitor"
                                            data-testid="switch-rework"
                                            defaultChecked={openDetailTextArea}
                                            onChange={handleChange}
                                        />
                                    </Grid> 
                                </Grid> */}

                                {/* {openDetailTextArea && ( */}
                                <Box className="rework-solicitor-message">
                                    <InputLabel id="rework-textarea-solicitor">
                                        This message will show on the email
                                        request *
                                    </InputLabel>

                                    <AppFormikTextArea
                                        testid="reworkreason"
                                        className="rework-reason-textarea"
                                        notched={false}
                                        maxLength={500}
                                        arialabelledby="rework-textarea-solicitor"
                                        name="reworktext"
                                        rows={4}
                                    />
                                </Box>
                                {/* )} */}
                            </Box>
                        )}
                        {isSaved && (
                            <Box className="container-document-rework-detail-saved">
                                <Typography className="solicitorname">{`${SolicitorDisplayed?.name} (${SolicitorDisplayed?.email.id})`}</Typography>
                                <Typography>
                                    had received an email requesting to
                                </Typography>
                                <Typography>
                                    re upload the documents below:
                                </Typography>
                                <ul>
                                    <li>{`${document?.placeholder}`}</li>
                                </ul>
                            </Box>
                        )}
                        {openDetail && !isSaved ? (
                            <Box className="action-to-complete-task-rework">
                                <Grid
                                    container
                                    spacing={2}
                                    className="action-container-complete-task"
                                >
                                    <Grid item xs={6} className="action-item">
                                        <AppPrimaryButton
                                            title="Go back"
                                            className="btn-small back"
                                            onClick={handleClose}
                                        />
                                    </Grid>
                                    <Grid item xs={6} className="action-item">
                                        <AppPrimaryButton
                                            title="Confirm & Proceed"
                                            className="btn-small"
                                            onClick={handleConfirm}
                                        />
                                    </Grid>
                                </Grid>
                            </Box>
                        ) : (
                            <Box className="action-to-close-task-rework">
                                <AppPrimaryButton
                                    title="Go back"
                                    className="btn-small back"
                                    disabled={
                                        NotExpectedMetaProps?.isActiveBackNavigation
                                    }
                                    onClick={handleCloseRework}
                                />
                            </Box>
                        )}
                    </Box>
                </Modal>
            </Box>
        );
    }
);

export default ReworkModal;
